// packages/studio/web/src/diff/DiffPackageTabs.tsx
//
// A changeset can span multiple ArchLang packages in a monorepo. The diff is
// computed per package; this tab strip lets the user switch between the changed
// packages. Hidden when only one package changed (the common case).

import type { ReactElement } from "react";
import { Badge } from "@archlang/ui";
import type { PackageDiff } from "./useGitDiff.js";
import { cx } from "../lib/classNames.js";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu.js";

type Translate = ReturnType<typeof useTranslation>["t"];

function labelFor(p: PackageDiff, t: Translate): string {
  if (p.name) return p.name;
  if (p.implicit || p.key === "") return t("tabs.repoRoot");
  return p.key;
}

export function DiffPackageTabs({
  packages,
  activeKey,
  onSelect,
}: {
  readonly packages: readonly PackageDiff[];
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}): ReactElement | null {
  const { t } = useTranslation("diff");
  const menu = useContextMenu();
  function itemsFor(p: PackageDiff): ContextMenuItem[] {
    const path = p.key || "";
    const label = labelFor(p, t);
    return [
      {
        label: t("tabs.menuCopyPath"),
        disabled: !path,
        onClick: () => void navigator.clipboard?.writeText(path),
      },
      {
        label: t("tabs.menuCopyName"),
        onClick: () => void navigator.clipboard?.writeText(label),
      },
    ];
  }
  if (packages.length <= 1) return null;
  return (
    <div
      role="tablist"
      aria-label={t("tabs.ariaLabel")}
      className="flex flex-wrap items-center gap-1 border-b border-slate-800 bg-slate-950 px-3 py-1.5"
    >
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
        {t("tabs.count", { count: packages.length })}
      </span>
      {packages.map((p) => {
        const errors = (p.diagnostics ?? []).filter((d) => d.severity === "error").length;
        return (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={p.key === activeKey}
            onClick={() => onSelect(p.key)}
            onContextMenu={(e) => menu.open(e, itemsFor(p))}
            className={cx(
              "flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors",
              p.key === activeKey
                ? "bg-arch-600/30 text-arch-200"
                : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200",
            )}
            title={p.key || t("tabs.repoRootTitle")}
          >
            {labelFor(p, t)}
            {errors > 0 ? (
              <span title={t("tabs.resolutionErrors", { count: errors })}>
                <Badge count={errors} tone="error" />
              </span>
            ) : null}
          </button>
        );
      })}
      {menu.node}
    </div>
  );
}
